import React, {
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useState,
  useRef
} from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Collapse,
  Paper
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  AlertBannerContext,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Product, ProductItem, ProductItemFormData } from "./types/products";
import { SellingMode } from "./enums/sellingMode";
import { Unit } from "./enums/unit";
import { DefaultProductItemFormValues } from "./constants/productItems";
import FormModal, {
  formFieldSx,
  formSectionTitleSx
} from "../common/FormModal/FormModal";
import {
  useCreateProductItem,
  isUserCompanyAdmin
} from "../../hooks/api/useProductItem";
import { toGraphQLBaseUnit } from "../../Utils/toGraphQLBaseUnit";
import { formatBaseUnitForDisplay } from "./utils/productUtils";
import {
  shortLabelYupTest,
  getPurchasePricePlaceholder,
  getSalesPricePlaceholder,
  getMinSalesPricePlaceholder,
  getTotalQuantityPlaceholder
} from "../../Utils/productValidation";
import {
  optionalNonNegativeNumber,
  requiredNonNegativeNumber
} from "./utils/productItemSchemaHelpers";
import { WizardPhase, BatchCreateResult } from "./types/productItemWizard";
import { getBatchQuantitySchema } from "./constants/productItemBatch";
import { createProductItemsInBatch } from "./utils/createProductItemsInBatch";
import AddProductItemQuantityStep from "./AddProductItemQuantityStep";
import ProductItemBatchResultView from "./ProductItemBatchResultView";
import { productItemFormDataFromBaseItem } from "./utils/productItemFormDataFromBaseItem";
import {
  getPerItemQuantityFieldLabel,
  getPricePerUnitSuffix,
  getProductUnitLabel
} from "./utils/getProductInventoryStatCardDescription";

interface AddProductItemModalProps {
  open: boolean;
  onClose: () => void;
  product: Product;
  onSubmit: (data: ProductItemFormData) => Promise<void>;
  isLoading?: boolean;
  /** When set, step 1 is prefilled from this item (clone flow). */
  cloneFromItem?: ProductItem;
  /**
   * COMPANY_ADMIN: catalog product's store (`product.storeId`).
   * Sent on create; no store selector is shown inside this form.
   */
  productCatalogStoreId?: string;
}

// ---------------------------------------------------------------------------
// Unit resolution
// ---------------------------------------------------------------------------

/**
 * Resolves a typed Unit enum value from the product's baseUnit string.
 * Returns undefined when the product has no recognised base unit.
 */
function unitFromProductBase(product: Product): Unit | undefined {
  const wire = toGraphQLBaseUnit(
    product.baseUnit ?? (product.unit as string | undefined)
  );
  if (!wire) return undefined;
  return (Object.values(Unit) as string[]).includes(wire)
    ? (wire as Unit)
    : undefined;
}

// ---------------------------------------------------------------------------
// Dynamic Yup schema
// ---------------------------------------------------------------------------

/**
 * Builds the product-item form schema for the given selling mode.
 * Called inside useMemo so it only rebuilds when sellingMode or t changes.
 *
 * NOTE: Yup's `.test()` accepts a plain config object `{ name, test }` which
 * is exactly what `shortLabelYupTest()` returns — no spread needed.
 */
const getProductItemSchema = (
  sellingMode: SellingMode,
  t?: (key: string, fallback: string) => string
) => {
  const tFn = t ?? ((_key: string, fallback: string) => fallback);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const baseSchema: Record<string, any> = {
    productId: Yup.string()
      .required(
        tFn("add-product-item-product-id-required", "Product ID is required")
      )
      .max(
        50,
        tFn(
          "add-product-item-product-id-max",
          "Product ID must be at most 50 characters"
        )
      ),

    purchasePrice: requiredNonNegativeNumber(
      tFn,
      "add-product-item-purchase-price-required",
      "Purchase price is required",
      "add-product-item-purchase-price-min",
      "Purchase price must be 0 or greater"
    ),

    salesPrice: requiredNonNegativeNumber(
      tFn,
      "add-product-item-sales-price-required",
      "Sales price is required",
      "add-product-item-sales-price-min",
      "Sales price must be 0 or greater"
    ),

    minSalesPrice: requiredNonNegativeNumber(
      tFn,
      "add-product-item-min-sales-price-required",
      "Min sales price is required",
      "add-product-item-min-sales-price-min",
      "Min sales price must be 0 or greater"
    ),

    variationName: Yup.string()
      .trim()
      .required(
        tFn("add-product-item-variation-required", "Item name is required")
      )
      .max(
        150,
        tFn(
          "add-product-item-variation-max",
          "Item name must be at most 150 characters"
        )
      )
      .test(
        shortLabelYupTest(
          tFn("add-product-item-variation-label", "Item name"),
          150
        )
      ),

    weight: optionalNonNegativeNumber(
      tFn,
      "add-product-item-weight-min",
      "Weight"
    ),
    width: optionalNonNegativeNumber(
      tFn,
      "add-product-item-width-min",
      "Width"
    ),
    height: optionalNonNegativeNumber(
      tFn,
      "add-product-item-height-min",
      "Height"
    ),
    thickness: optionalNonNegativeNumber(
      tFn,
      "add-product-item-thickness-min",
      "Thickness"
    ),

    color: Yup.string()
      .max(
        100,
        tFn(
          "add-product-item-color-max",
          "Color must be at most 100 characters"
        )
      )
      .optional(),

    packSize: Yup.string()
      .max(
        50,
        tFn(
          "add-product-item-pack-size-max",
          "Pack size must be at most 50 characters"
        )
      )
      .optional(),

    material: Yup.string()
      .max(
        100,
        tFn(
          "add-product-item-material-max",
          "Material must be at most 100 characters"
        )
      )
      .optional(),

    packagingType: Yup.string()
      .max(
        100,
        tFn(
          "add-product-item-packaging-type-max",
          "Packaging type must be at most 100 characters"
        )
      )
      .optional(),

    expirationDate: Yup.string().optional(),
    productionDate: Yup.string().optional(),

    lotNumber: Yup.string()
      .max(
        100,
        tFn(
          "add-product-item-lot-max",
          "Lot number must be at most 100 characters"
        )
      )
      .optional()
  };

  if (sellingMode === SellingMode.SINGLE_ITEM) {
    baseSchema.totalQuantity = Yup.number()
      .required()
      .oneOf(
        [1],
        tFn(
          "add-product-item-single-quantity",
          "Single item products must have quantity of 1"
        )
      );
  } else if (sellingMode === SellingMode.UNIT_BASED) {
    baseSchema.unit = Yup.mixed<Unit>().oneOf(
      [Unit.UNIT],
      tFn(
        "add-product-item-unit-based-unit",
        "Unit must be UNIT for unit-based products"
      )
    );
    baseSchema.totalQuantity = Yup.number()
      .transform((val, orig) =>
        orig === "" || orig === null || orig === undefined ? undefined : val
      )
      .required(
        tFn(
          "add-product-item-total-quantity-required",
          "Total quantity is required"
        )
      )
      .min(
        1,
        tFn(
          "add-product-item-total-quantity-min",
          "Total quantity must be at least 1"
        )
      )
      .test(
        "whole-units",
        tFn(
          "add-product-item-whole-units",
          "Total quantity must be a whole number"
        ),
        (value) =>
          value === undefined ||
          value === null ||
          (Number.isFinite(value) && Math.floor(value) === value)
      );
  } else if (sellingMode === SellingMode.MEASURED) {
    baseSchema.totalQuantity = Yup.number()
      .transform((val, orig) =>
        orig === "" || orig === null || orig === undefined ? undefined : val
      )
      .required(
        tFn(
          "add-product-item-total-quantity-required",
          "Total quantity is required"
        )
      )
      .moreThan(
        0,
        tFn(
          "add-product-item-measured-quantity-positive",
          "Total quantity must be greater than 0"
        )
      );
  }

  return Yup.object().shape(
    baseSchema
  ) as Yup.ObjectSchema<ProductItemFormData>;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AddProductItemModal: React.FC<AddProductItemModalProps> = ({
  open,
  onClose,
  product,
  onSubmit,
  isLoading = false,
  productCatalogStoreId,
  cloneFromItem
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((s: RootState) => s.user.userDetails);
  const isCompanyAdmin = isUserCompanyAdmin(userDetails);
  const catalogStoreId = productCatalogStoreId?.trim() ?? "";

  const { sellingMode } = product;
  const createProductItem = useCreateProductItem();

  const effectiveUnit = unitFromProductBase(product);

  const validationSchema = useMemo(
    () => getProductItemSchema(sellingMode, t),
    [sellingMode, t]
  );

  const getDefaultValues = useCallback((): ProductItemFormData => {
    if (cloneFromItem) {
      return productItemFormDataFromBaseItem(cloneFromItem, product);
    }

    const defaults: ProductItemFormData = {
      ...DefaultProductItemFormValues,
      productId: product.id,
      variationName: product.name ?? "",
      storeId: ""
    };

    if (sellingMode === SellingMode.SINGLE_ITEM) {
      defaults.totalQuantity = 1;
    } else if (sellingMode === SellingMode.UNIT_BASED) {
      defaults.totalQuantity = 1;
      defaults.unit = Unit.UNIT;
    } else if (sellingMode === SellingMode.MEASURED) {
      defaults.totalQuantity = 1;
    }

    return defaults;
  }, [cloneFromItem, product, sellingMode]);

  const hasOptionalDetails = useCallback((data: ProductItemFormData) => {
    return !!(
      data.weight ||
      data.height ||
      data.width ||
      data.thickness ||
      data.color ||
      data.material ||
      data.packagingType ||
      data.packSize ||
      data.expirationDate ||
      data.productionDate ||
      data.lotNumber
    );
  }, []);

  const [phase, setPhase] = useState<WizardPhase>("specs");
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);
  const [batchQuantity, setBatchQuantity] = useState(1);
  const [batchResult, setBatchResult] = useState<BatchCreateResult | null>(
    null
  );
  const [savedFormData, setSavedFormData] =
    useState<ProductItemFormData | null>(null);
  const [quantityError, setQuantityError] = useState<string | undefined>();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ProductItemFormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: getDefaultValues()
  });

  const buildProductItemPayload = useCallback(
    (data: ProductItemFormData): ProductItemFormData => {
      const inheritedUnit = unitFromProductBase(product);
      return {
        ...data,
        storeId: isCompanyAdmin ? catalogStoreId : data.storeId,
        unit:
          sellingMode === SellingMode.MEASURED
            ? inheritedUnit
            : sellingMode === SellingMode.UNIT_BASED
              ? Unit.UNIT
              : data.unit
      };
    },
    [catalogStoreId, isCompanyAdmin, product, sellingMode]
  );

  const wasOpenRef = useRef(false);
  const batchInFlightRef = useRef(false);

  const resetWizard = useCallback(() => {
    const defaults = getDefaultValues();
    reset(defaults);
    setPhase("specs");
    setBatchQuantity(1);
    setBatchResult(null);
    setSavedFormData(null);
    setQuantityError(undefined);
    setShowOptionalDetails(hasOptionalDetails(defaults));
    batchInFlightRef.current = false;
  }, [getDefaultValues, hasOptionalDetails, reset]);

  useEffect(() => {
    // Reset wizard only when the modal opens — not on parent re-renders while open
    // (e.g. list refresh after batch create would otherwise wipe the QR results step).
    if (open && !wasOpenRef.current) {
      resetWizard();
    }
    wasOpenRef.current = open;
  }, [open, resetWizard]);

  useEffect(() => {
    return () => {
      reset(DefaultProductItemFormValues);
    };
  }, [reset]);

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const handleCloseAttempt = () => {
    if (phase === "creating") return;
    handleClose();
  };

  const handleNext: SubmitHandler<ProductItemFormData> = (data) => {
    if (isCompanyAdmin && !catalogStoreId) {
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.STORE_ID_REQUIRED
      });
      return;
    }
    setSavedFormData(buildProductItemPayload(data));
    setBatchQuantity(1);
    setQuantityError(undefined);
    setPhase("quantity");
  };

  const validateBatchQuantity = (quantity: number): string | undefined => {
    try {
      getBatchQuantitySchema(t).validateSync({ quantity });
      return undefined;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        return err.message;
      }
      return t(
        "add-items-in-stock-quantity-min",
        "Quantity must be at least 1"
      );
    }
  };

  const handleBatchCreate = async () => {
    if (batchInFlightRef.current || phase === "creating") {
      return;
    }
    const validationMessage = validateBatchQuantity(batchQuantity);
    if (validationMessage) {
      setQuantityError(validationMessage);
      return;
    }
    if (isCompanyAdmin && !catalogStoreId) {
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.STORE_ID_REQUIRED
      });
      return;
    }
    if (!savedFormData) return;

    batchInFlightRef.current = true;
    setPhase("creating");
    try {
      const result = await createProductItemsInBatch(
        (input) => createProductItem.mutateAsync(input),
        savedFormData,
        batchQuantity
      );
      setBatchResult(result);
      setPhase("results");

      try {
        await onSubmit(savedFormData);
      } catch (refreshError) {
        console.error("Failed to refresh list after create:", refreshError);
      }
    } catch (error) {
      console.error("Failed to create product item(s):", error);
      setPhase("quantity");
    } finally {
      batchInFlightRef.current = false;
    }
  };

  const handleAddMore = () => {
    setPhase("specs");
    setBatchResult(null);
    setQuantityError(undefined);
    if (savedFormData) {
      reset(savedFormData);
    }
  };

  const formLoading =
    isLoading ||
    isSubmitting ||
    createProductItem.isPending ||
    phase === "creating";

  const wizardSteps = [
    t("add-product-item-wizard-step-details", "Item details"),
    t("add-product-item-wizard-step-quantity", "How many items"),
    t("add-product-item-wizard-step-qr", "Print labels")
  ];

  const activeStep =
    phase === "specs"
      ? 0
      : phase === "quantity" || phase === "creating"
        ? 1
        : 2;

  const unitForPlaceholders = effectiveUnit ?? product.baseUnit;
  const catalogUnitLabel = getProductUnitLabel(product, t);
  const perItemQuantityFieldLabel = getPerItemQuantityFieldLabel(
    sellingMode,
    catalogUnitLabel,
    t
  );
  const pricePerSuffix = getPricePerUnitSuffix(
    sellingMode,
    catalogUnitLabel,
    t
  );
  const purchasePricePlaceholder = getPurchasePricePlaceholder(
    unitForPlaceholders,
    t
  );
  const salesPricePlaceholder = getSalesPricePlaceholder(
    unitForPlaceholders,
    t
  );
  const minSalesPricePlaceholder = getMinSalesPricePlaceholder(
    unitForPlaceholders,
    t
  );
  const totalQtyPlaceholder = getTotalQuantityPlaceholder(
    unitForPlaceholders,
    t
  );

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderField = (
    name: keyof ProductItemFormData,
    label: string,
    type: string = "text",
    disabled: boolean = false,
    placeholder?: string
  ) => {
    const isNumeric = [
      "totalQuantity",
      "purchasePrice",
      "salesPrice",
      "minSalesPrice",
      "weight",
      "height",
      "width",
      "thickness"
    ].includes(name as string);

    const isDate = type === "date";

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size={matchMD ? "small" : "medium"}
            label={label}
            type={type}
            placeholder={placeholder}
            disabled={disabled || formLoading}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            inputProps={isNumeric ? { min: 0, step: 0.01 } : {}}
            InputLabelProps={isDate ? { shrink: true } : undefined}
            sx={formFieldSx}
            value={field.value ?? ""}
          />
        )}
      />
    );
  };

  const showTotalQuantity =
    sellingMode === SellingMode.UNIT_BASED ||
    sellingMode === SellingMode.MEASURED;
  const isTotalQuantityDisabled = sellingMode === SellingMode.SINGLE_ITEM;
  const showInheritedBaseUnit = sellingMode === SellingMode.MEASURED;
  const showUnitFieldDisabled = sellingMode === SellingMode.UNIT_BASED;
  const showHeightField = sellingMode === SellingMode.MEASURED;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (phase === "results" && batchResult) {
    return (
      <ProductItemBatchResultView
        open={open}
        onClose={handleClose}
        onAddMore={handleAddMore}
        productName={product.name}
        batchResult={batchResult}
        flow={cloneFromItem ? "clone" : "wizard"}
      />
    );
  }

  return (
    <FormModal
      open={open}
      onClose={handleCloseAttempt}
      title={
        cloneFromItem
          ? t("clone-item-title", "Clone this item")
          : t("add-product-item-title", "Add item(s) in stock")
      }
      dataTestId="add-product-item-modal"
      footerAlign="center"
      icon={
        cloneFromItem ? (
          <ContentCopyOutlinedIcon sx={{ color: BRAND_COLOR, fontSize: 28 }} />
        ) : (
          <Inventory2RoundedIcon sx={{ color: BRAND_COLOR, fontSize: 28 }} />
        )
      }
      footer={
        phase === "creating" ? null : phase === "quantity" ? (
          <>
            <Button
              type="button"
              variant="outlined"
              onClick={() => setPhase("specs")}
              disabled={formLoading}
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "text.secondary",
                  backgroundColor: "action.hover"
                }
              }}
            >
              {t("add-product-item-wizard-back", "Back")}
            </Button>
            <LoadingButton
              type="button"
              variant="contained"
              loading={formLoading}
              onClick={handleBatchCreate}
              size={matchMD ? "medium" : "large"}
              data-testid="add-product-item-create-batch"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
                backgroundColor: BRAND_COLOR,
                boxShadow: "none",
                "&:hover": { backgroundColor: BRAND_HOVER, boxShadow: "none" }
              }}
            >
              {batchQuantity === 1
                ? t("add-product-item-wizard-create-one", "Create 1 item")
                : t(
                    "add-product-item-wizard-create-button",
                    "Create {{count}} items",
                    { count: batchQuantity }
                  )}
            </LoadingButton>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCloseAttempt}
              disabled={formLoading}
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "text.secondary",
                  backgroundColor: "action.hover"
                }
              }}
            >
              {t("add-product-item-cancel", "Cancel")}
            </Button>
            <LoadingButton
              form="add-product-item-form"
              type="submit"
              variant="contained"
              loading={formLoading}
              size={matchMD ? "medium" : "large"}
              data-testid="add-product-item-submit"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
                backgroundColor: BRAND_COLOR,
                boxShadow: "none",
                "&:hover": { backgroundColor: BRAND_HOVER, boxShadow: "none" }
              }}
            >
              {t("add-product-item-wizard-next", "Next")}
            </LoadingButton>
          </>
        )
      }
    >
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {wizardSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {phase === "creating" && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography color="text.secondary">
            {batchQuantity === 1
              ? t("add-product-item-wizard-creating-one", "Creating 1 item…")
              : t(
                  "add-product-item-wizard-creating",
                  "Creating {{count}} items…",
                  { count: batchQuantity }
                )}
          </Typography>
        </Box>
      )}

      {phase === "quantity" && savedFormData && (
        <AddProductItemQuantityStep
          product={product}
          formData={savedFormData}
          quantity={batchQuantity}
          onQuantityChange={(value) => {
            setBatchQuantity(value);
            setQuantityError(undefined);
          }}
          quantityError={quantityError}
          disabled={formLoading}
        />
      )}

      {phase === "specs" && (
        <Box
          component="form"
          id="add-product-item-form"
          onSubmit={handleSubmit(handleNext)}
          noValidate
          sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          {cloneFromItem ? (
            <Paper
              variant="outlined"
              data-testid="clone-item-source-banner"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: "divider",
                backgroundColor: "action.hover"
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                {t("clone-item-started-from", "Started from")}
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
                {(cloneFromItem.variationName?.trim() || product.name) ?? ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  "clone-item-edit-hint",
                  "Details are prefilled from the item you selected. Change anything you need, then continue to choose how many items to create."
                )}
              </Typography>
            </Paper>
          ) : null}

          {/* ── Quantity / Unit ──────────────────────────────────────────────── */}
          <Typography component="span" sx={formSectionTitleSx}>
            {t("add-product-item-section-required", "Required")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {showTotalQuantity &&
              renderField(
                "totalQuantity",
                `${perItemQuantityFieldLabel}*`,
                "number",
                false,
                totalQtyPlaceholder
              )}

            {isTotalQuantityDisabled && (
              <Controller
                name="totalQuantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={perItemQuantityFieldLabel}
                    value={field.value ?? 1}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    onChange={() => field.onChange(1)}
                    slotProps={{ input: { readOnly: true } }}
                    helperText={t(
                      "add-product-item-single-item-quantity-helper",
                      "Single item mode: quantity is always 1"
                    )}
                    sx={{
                      ...formFieldSx,
                      "& .MuiInputBase-input": { cursor: "not-allowed" }
                    }}
                  />
                )}
              />
            )}

            {showInheritedBaseUnit && (
              <TextField
                label={t(
                  "add-product-item-base-unit-from-product",
                  "Base unit (from product)"
                )}
                value={formatBaseUnitForDisplay(
                  product.baseUnit ?? (product.unit as string | undefined)
                )}
                fullWidth
                size={matchMD ? "small" : "medium"}
                disabled
                helperText={t(
                  "add-product-item-base-unit-helper",
                  "Inherited from the product catalog. Quantities use this unit."
                )}
                sx={formFieldSx}
              />
            )}

            {showUnitFieldDisabled && (
              <TextField
                label={t("add-product-item-unit-label", "Unit")}
                value={Unit.UNIT}
                fullWidth
                size={matchMD ? "small" : "medium"}
                disabled
                helperText={t(
                  "add-product-item-unit-based-helper",
                  "Unit based mode. Unit is always UNIT"
                )}
                sx={formFieldSx}
              />
            )}
          </Box>

          {/* ── Identifiers & Pricing ────────────────────────────────────────── */}
          <Typography component="span" sx={{ ...formSectionTitleSx, mt: 3 }}>
            {t(
              "add-product-item-section-identifiers-pricing",
              "Label & prices"
            )}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2
            }}
          >
            {renderField(
              "variationName",
              t("add-product-item-variation-name", "Item name*"),
              "text",
              false,
              t(
                "add-product-item-variation-placeholder",
                "e.g. 500ml bottle, Red size M"
              )
            )}
            {renderField(
              "purchasePrice",
              t(
                "add-product-item-purchase-price",
                "Purchase price* ({{suffix}})",
                { suffix: pricePerSuffix }
              ),
              "number",
              false,
              purchasePricePlaceholder
            )}
            {renderField(
              "salesPrice",
              t("add-product-item-sales-price", "Sales price* ({{suffix}})", {
                suffix: pricePerSuffix
              }),
              "number",
              false,
              salesPricePlaceholder
            )}
            {renderField(
              "minSalesPrice",
              t(
                "add-product-item-min-sales-price",
                "Min sales price* ({{suffix}})",
                { suffix: pricePerSuffix }
              ),
              "number",
              false,
              minSalesPricePlaceholder
            )}
          </Box>

          <Button
            type="button"
            variant="text"
            onClick={() => setShowOptionalDetails((prev) => !prev)}
            endIcon={
              showOptionalDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />
            }
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              color: "text.secondary",
              px: 0,
              mt: 2
            }}
            data-testid="add-product-item-optional-details-toggle"
          >
            {showOptionalDetails
              ? t(
                  "add-product-item-hide-optional-details",
                  "Hide optional details"
                )
              : t(
                  "add-product-item-show-optional-details",
                  "More details (optional)"
                )}
          </Button>

          <Collapse in={showOptionalDetails}>
            {/* ── Physical Attributes ──────────────────────────────────────────── */}
            <Typography component="span" sx={{ ...formSectionTitleSx, mt: 1 }}>
              {t(
                "add-product-item-section-physical-attributes",
                "Physical Attributes"
              )}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2
              }}
            >
              {renderField(
                "weight",
                t("add-product-item-weight", "Weight"),
                "number",
                false,
                t("add-product-item-weight-placeholder", "e.g. 0.5 (kg)")
              )}
              {showHeightField &&
                renderField(
                  "height",
                  t("add-product-item-height", "Height"),
                  "number",
                  false,
                  t("add-product-item-height-placeholder", "e.g. 20 (cm)")
                )}
              {renderField(
                "width",
                t("add-product-item-width", "Width"),
                "number",
                false,
                t("add-product-item-width-placeholder", "e.g. 10 (cm)")
              )}
              {renderField(
                "thickness",
                t("add-product-item-thickness", "Thickness"),
                "number",
                false,
                t("add-product-item-thickness-placeholder", "e.g. 2 (cm)")
              )}
              {renderField(
                "color",
                t("add-product-item-color", "Color"),
                "text",
                false,
                t("add-product-item-color-placeholder", "e.g. Red, Blue")
              )}
              {renderField(
                "material",
                t("add-product-item-material", "Material"),
                "text",
                false,
                t(
                  "add-product-item-material-placeholder",
                  "e.g. Cotton, Plastic"
                )
              )}
              {renderField(
                "packagingType",
                t("add-product-item-packaging-type", "Packaging Type"),
                "text",
                false,
                t(
                  "add-product-item-packaging-placeholder",
                  "e.g. Box, Bag, Blister"
                )
              )}
              {renderField(
                "packSize",
                t("add-product-item-pack-size", "Pack Size"),
                "text",
                false,
                t(
                  "add-product-item-pack-size-placeholder",
                  "e.g. 12-pack, 500g bag"
                )
              )}
            </Box>

            {/* ── Dates & Lot ──────────────────────────────────────────────────── */}
            <Typography component="span" sx={{ ...formSectionTitleSx, mt: 3 }}>
              {t("add-product-item-section-dates-lot", "Dates & Lot")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2
              }}
            >
              {renderField(
                "expirationDate",
                t("add-product-item-expiration-date", "Expiration Date"),
                "date"
              )}
              {renderField(
                "productionDate",
                t("add-product-item-production-date", "Production Date"),
                "date"
              )}
              {renderField(
                "lotNumber",
                t("add-product-item-lot-number", "Lot Number"),
                "text",
                false,
                t("add-product-item-lot-placeholder", "e.g. LOT-2024-001")
              )}
            </Box>
          </Collapse>
        </Box>
      )}
    </FormModal>
  );
};

export default AddProductItemModal;
